import { useState, useEffect, useRef, useMemo } from 'react'
import { Save, X, Trash2, AlertTriangle, CheckCircle, AlertCircle, UploadCloud, Eye, Loader2 } from 'lucide-react'
import DashboardLayout from '../components/DashboardLayout'
import ChangePasswordModal from '../components/ChangePasswordModal'
import SubscriptionManagement from '../components/SubscriptionManagement'
import PlayerCardPreview from '../components/PlayerCardPreview'
import api from '../services/api'
import clubService from '../services/clubService'
import { KIT_COLORS, bordureCouleur, pastilleCouleur } from '../utils/kitColors'
import { getPlayerCategories } from '../constants/categories'
import { COMPETITION_LEVELS_BY_CATEGORY, ALL_COMPETITION_LEVELS } from '../constants/competitionLevels'
import { usePresignedLogo } from '../hooks/usePresignedLogo'
import { T, globalStyles } from '../theme'
import { useSeason } from '../context/SeasonContext'

const G = {
  bg:     T.bg,       bg2:    T.surface,
  gold:   T.gold,     goldD:  T.goldD,
  goldBg: T.goldBg,   goldBdr:T.goldBdr,
  mono:   T.mono,     display:T.display,
  border: T.ruleDark,
  muted:  T.muted2,
  text:   T.ink,
}

const inputStyle = {
  width: '100%', background: T.surface,
  border: `0.5px solid ${T.rule}`,
  padding: '11px 14px', color: T.ink,
  fontFamily: T.mono, fontSize: 13,
  outline: 'none', boxSizing: 'border-box',
  borderRadius: T.radius, minHeight: 44,
}

function Section({ title, children, accent, isMobile }) {
  return (
    <div style={{ background: G.bg2, border: `0.5px solid ${G.border}`, borderTop: `2px solid ${accent || T.dark}`, borderRadius: T.radius, padding: isMobile ? '20px 16px' : '24px' }}>
      <div style={{ fontFamily: G.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.14em', textTransform: 'uppercase', color: T.muted, marginBottom: 18 }}>
        {title}
      </div>
      {children}
    </div>
  )
}

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return ''
  if (bytes < 1024) return `${bytes} o`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`
}

export default function ClubSettings() {
  const [isMobile, setIsMobile] = useState(false)
  const [isNarrow, setIsNarrow] = useState(false) // < 1024 -> preview empilee
  const [club, setClub] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [showPwdModal, setShowPwdModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState('')
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [uploadMeta, setUploadMeta] = useState(null) // { name, size } du dernier upload
  const [formData, setFormData] = useState({ name: '', primary_color: '#4F8EF7', secondary_color: '#0f0f0d', kit_color_home: '', kit_color_away: '' })
  const [initial, setInitial] = useState(null)
  const [categories, setCategories] = useState([])
  const [teamLevels, setTeamLevels] = useState({})
  const [teamLevelsSaving, setTeamLevelsSaving] = useState({})
  const [previewCategory, setPreviewCategory] = useState('')
  const [toast, setToast] = useState(null)
  const fileRef = useRef(null)
  const { previewUrl: logoPreviewUrl, loading: previewLoading, fetchPreview, reset: resetPreview } = usePresignedLogo()
  const { currentSeason } = useSeason()

  useEffect(() => { loadClub() }, [])
  useEffect(() => {
    const check = () => {
      setIsMobile(window.innerWidth < 768)
      setIsNarrow(window.innerWidth < 1024)
    }
    check(); window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  const loadClub = async () => {
    try {
      setLoading(true)
      const [data, cats, levelsRes] = await Promise.all([
        clubService.getMyClub(),
        getPlayerCategories(),
        api.get('/club/competition-levels'),
      ])
      setClub(data)
      const next = {
        name: data.name || '',
        primary_color: data.primary_color || '#4F8EF7',
        secondary_color: data.secondary_color || '#0f0f0d',
        kit_color_home: data.kit_color_home || '',
        kit_color_away: data.kit_color_away || '',
      }
      setFormData(next)
      setInitial(next)
      if (data.logo_url) fetchPreview()
      else resetPreview()

      setCategories(cats)
      setPreviewCategory(prev => prev || cats[0] || '')
      const map = {}
      cats.forEach(c => { map[c] = '' })
      levelsRes.data.forEach(r => { if (r.category in map) map[r.category] = r.competition_level || '' })
      setTeamLevels(map)
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }

  const dirty = useMemo(() => {
    if (!initial) return false
    return ['name', 'primary_color', 'secondary_color', 'kit_color_home', 'kit_color_away']
      .some(k => formData[k] !== initial[k])
  }, [formData, initial])

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))
  const showToast = (type, msg) => { setToast({ type, msg }); setTimeout(() => setToast(null), 3000) }

  const handleFile = async (file) => {
    if (!file) return
    if (!file.type.startsWith('image/')) { showToast('error', 'Choisis une image (PNG, JPG, SVG)'); return }
    setUploading(true)
    try {
      await clubService.uploadLogo(file)
      setUploadMeta({ name: file.name, size: file.size })
      const data = await clubService.getMyClub()
      setClub(data)
      // force=true : bypass le cache module-level (BUG-003) — le logo vient
      // d'être remplacé, la presigned précédente est obsolète.
      await fetchPreview({ force: true })
      showToast('success', 'Logo enregistré ✓')
    } catch (e) {
      const detail = e?.response?.data?.detail
      showToast('error', typeof detail === 'string' ? detail : 'Upload du logo impossible')
    } finally {
      setUploading(false)
    }
  }

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false)
    const f = e.dataTransfer?.files?.[0]
    if (f) handleFile(f)
  }

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'SUPPRIMER') return
    setDeleteLoading(true)
    try {
      const token = localStorage.getItem('insightball_token')
      const API = import.meta.env.VITE_API_URL || 'https://backend-pued.onrender.com/api'
      const res = await fetch(`${API}/account/delete`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!res.ok) throw new Error()
      localStorage.removeItem('insightball_token')
      window.location.href = '/'
    } catch {
      showToast('error', 'Erreur lors de la suppression')
      setDeleteLoading(false)
    }
  }

  const handleTeamLevelSave = async (category) => {
    setTeamLevelsSaving(prev => ({ ...prev, [category]: 'saving' }))
    try {
      await api.patch(`/club/competition-levels/${encodeURIComponent(category)}`, {
        competition_level: teamLevels[category] || null,
      })
      setTeamLevelsSaving(prev => ({ ...prev, [category]: 'saved' }))
      setTimeout(() => setTeamLevelsSaving(prev => ({ ...prev, [category]: 'idle' })), 2500)
    } catch (e) {
      setTeamLevelsSaving(prev => ({ ...prev, [category]: 'idle' }))
      showToast('error', `Erreur lors de la sauvegarde du niveau ${category}`)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      await clubService.updateClub({
        name: formData.name,
        primary_color: formData.primary_color,
        secondary_color: formData.secondary_color,
        // Chaine vide -> null · le backend n'accepte que les 11 valeurs, et
        // null est la facon d'effacer un jeu.
        kit_color_home: formData.kit_color_home || null,
        kit_color_away: formData.kit_color_away || null,
      })
      showToast('success', 'Paramètres sauvegardés ✓')
      await loadClub()
    } catch (e) {
      console.error(e)
      const detail = e?.response?.data?.detail
      showToast('error', typeof detail === 'string' ? detail : 'Erreur lors de la sauvegarde')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return (
    <DashboardLayout>
      <div style={{ textAlign: 'center', padding: '80px 0', fontFamily: G.mono, fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase', color: G.muted }}>Chargement...</div>
    </DashboardLayout>
  )

  const hasLogo = !!(club?.logo_url)
  const previewScale = isMobile ? Math.min(1, (Math.min(window.innerWidth, 480) - 48) / 380) : 1

  const previewCol = (
    <div style={{
      width: isNarrow ? '100%' : 420,
      flexShrink: 0,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
      ...(isNarrow ? {} : { position: 'sticky', top: 40, alignSelf: 'flex-start' }),
    }}>
      <div style={{ fontFamily: G.mono, fontSize: 9, letterSpacing: '.18em', textTransform: 'uppercase', color: G.muted, alignSelf: isNarrow ? 'center' : 'flex-start' }}>
        Aperçu de la carte joueur
      </div>
      <div style={{ width: 380 * previewScale, height: 676 * previewScale }}>
        <div style={{ transform: `scale(${previewScale})`, transformOrigin: 'top left' }}>
          <PlayerCardPreview
            primaryColor={formData.primary_color}
            secondaryColor={formData.secondary_color}
            competitionLevel={teamLevels[previewCategory] || ''}
            clubName={formData.name}
            logoUrl={logoPreviewUrl}
            season={currentSeason}
          />
        </div>
      </div>
      {categories.length > 1 && (
        <div style={{ width: isNarrow ? '100%' : 380, maxWidth: 380 }}>
          <div style={{ fontFamily: G.mono, fontSize: 8, letterSpacing: '.12em', textTransform: 'uppercase', color: G.muted, marginBottom: 6 }}>
            Aperçu pour la catégorie
          </div>
          <select
            value={previewCategory}
            onChange={e => setPreviewCategory(e.target.value)}
            style={{ ...inputStyle, fontSize: 11, cursor: 'pointer' }}
            onFocus={e => e.target.style.borderColor = T.ink}
            onBlur={e => e.target.style.borderColor = T.rule}
          >
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      )}
      <div style={{ fontFamily: G.mono, fontSize: 9, color: G.muted, textAlign: 'center', lineHeight: 1.7, maxWidth: 340 }}>
        Le joueur est un exemple. Ce que tes joueurs reçoivent reprend tes couleurs, ton logo et le niveau de leur catégorie.
      </div>
    </div>
  )

  return (
    <DashboardLayout>
      <style>{globalStyles}</style>

      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          display: 'flex', alignItems: 'center', gap: 10, padding: '14px 20px',
          background: T.dark,
          border: `1px solid ${toast.type === 'success' ? 'rgba(34,197,94,0.35)' : 'rgba(239,68,68,0.35)'}`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
        }}>
          {toast.type === 'success'
            ? <CheckCircle size={15} color="#22c55e" />
            : <AlertCircle size={15} color="#ef4444" />}
          <span style={{ fontFamily: G.mono, fontSize: 11, letterSpacing: '.06em', color: toast.type === 'success' ? '#22c55e' : '#ef4444' }}>
            {toast.msg}
          </span>
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 28, paddingBottom: 20, borderBottom: `0.5px solid ${T.rule}` }}>
        <p style={{ fontFamily: T.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.15em', textTransform: 'uppercase', color: T.muted, marginBottom: 8 }}>
          Paramètres du club
        </p>
        <h1 style={{ fontFamily: T.display, fontSize: 'clamp(28px, 5vw, 36px)', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '-.01em', lineHeight: 1, color: T.ink, margin: 0 }}>
          Identité du club.
        </h1>
      </div>

      <div style={{ display: 'flex', flexDirection: isNarrow ? 'column' : 'row', gap: 28, alignItems: 'flex-start' }}>
        {/* Colonne formulaire */}
        <div style={{ flex: 1, minWidth: 0, maxWidth: isNarrow ? '100%' : 680, display: 'flex', flexDirection: 'column', gap: 8 }}>

          {/* Nom */}
          <Section title="Nom du club" isMobile={isMobile}>
            <input type="text" name="name" value={formData.name} onChange={handleChange}
              placeholder="Mon Club FC" style={inputStyle}
              onFocus={e => e.target.style.borderColor = T.ink}
              onBlur={e => e.target.style.borderColor = T.rule} />
          </Section>

          {/* Logo */}
          <Section title="Logo du club" isMobile={isMobile}>
            {hasLogo ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px', background: 'rgba(22,163,74,0.06)', border: '1px solid rgba(22,163,74,0.25)', flexWrap: 'wrap' }}>
                <CheckCircle size={18} color={T.green} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: G.mono, fontSize: 11, color: T.green, letterSpacing: '.04em' }}>
                    Logo enregistré
                  </div>
                  {uploadMeta && (
                    <div style={{ fontFamily: G.mono, fontSize: 9, color: G.muted, marginTop: 3, wordBreak: 'break-all' }}>
                      {uploadMeta.name} · {fmtSize(uploadMeta.size)}
                    </div>
                  )}
                </div>
                <button onClick={() => { setShowPreviewModal(true); fetchPreview() }} style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                  background: 'transparent', border: `0.5px solid ${T.rule}`, color: T.muted,
                  fontFamily: T.mono, fontSize: 9, cursor: 'pointer', borderRadius: T.radius,
                }}>
                  <Eye size={12} /> Voir l'aperçu
                </button>
                <button onClick={() => fileRef.current?.click()} style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontFamily: G.mono, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', color: G.muted,
                }}>
                  Changer
                </button>
              </div>
            ) : (
              <div
                onClick={() => !uploading && fileRef.current?.click()}
                onDragOver={e => { e.preventDefault(); setDragOver(true) }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10,
                  padding: '40px 20px', cursor: uploading ? 'wait' : 'pointer',
                  background: dragOver ? G.goldBg : T.bgAlt,
                  border: `2px dashed ${dragOver ? G.gold : G.border}`,
                  transition: 'all .15s', textAlign: 'center',
                }}
              >
                {uploading
                  ? <Loader2 size={24} color={G.gold} style={{ animation: 'spin 1s linear infinite' }} />
                  : <UploadCloud size={24} color={G.muted} />}
                <div style={{ fontFamily: G.mono, fontSize: 12, color: G.text }}>
                  {uploading ? 'Envoi du logo...' : 'Glisse ton logo ici ou clique pour parcourir'}
                </div>
                <div style={{ fontFamily: G.mono, fontSize: 9, color: G.muted }}>
                  PNG, SVG ou JPG. Le fond noir est nettoyé automatiquement.
                </div>
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*" hidden
              onChange={e => { handleFile(e.target.files?.[0]); e.target.value = '' }} />
          </Section>

          {/* Couleurs */}
          <Section title="Couleurs du club" isMobile={isMobile}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 20 }}>
              {[{ label: 'Couleur principale', name: 'primary_color' }, { label: 'Couleur secondaire', name: 'secondary_color' }].map(({ label, name }) => (
                <div key={name}>
                  <div style={{ fontFamily: G.mono, fontSize: 8, letterSpacing: '.18em', textTransform: 'uppercase', color: T.muted2, marginBottom: 10 }}>{label}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input type="color" name={name} value={formData[name]} onChange={handleChange}
                      style={{ width: 44, height: 44, border: `0.5px solid ${T.rule}`, background: 'transparent', cursor: 'pointer', padding: 2, borderRadius: T.radius }} />
                    <input type="text" name={name} value={formData[name]} onChange={handleChange} style={{ ...inputStyle, flex: 1 }}
                      onFocus={e => e.target.style.borderColor = T.ink}
                      onBlur={e => e.target.style.borderColor = T.rule} />
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* COULEURS-MAILLOT-001 · A NE PAS CONFONDRE avec la section
              ci-dessus. « Couleurs du club » sont des hex de MARQUE, utilisés
              par la carte joueur. Ici, ce sont les MAILLOTS réellement portés :
              ils partent à l'analyse pour reconnaître ton équipe dans la vidéo,
              et n'acceptent que onze valeurs. Les deux jeux existent parce
              qu'on ne joue pas dans la même tenue chez soi et à l'extérieur. */}
          <Section title="Tes maillots" isMobile={isMobile}>
            <p style={{ fontFamily: G.mono, fontSize: 10, color: T.muted2, lineHeight: 1.65, margin: '0 0 18px' }}>
              Ce sont les couleurs proposées par défaut à la création d'un match.
              Tu peux les changer match par match : un jeu de coupe, une couleur
              imposée par l'arbitre ou un nouveau sponsor ne remettent pas ceci
              en cause.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 20 }}>
              {[{ label: 'À domicile', name: 'kit_color_home' }, { label: 'À l\'extérieur', name: 'kit_color_away' }].map(({ label, name }) => (
                <div key={name}>
                  <div style={{ fontFamily: G.mono, fontSize: 8, letterSpacing: '.18em', textTransform: 'uppercase', color: T.muted2, marginBottom: 10 }}>{label}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span aria-hidden="true" style={{
                      width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                      background: pastilleCouleur(formData[name]) || 'transparent',
                      border: `1px solid ${formData[name] ? bordureCouleur(formData[name]) : T.rule}`,
                    }} />
                    <select name={name} value={formData[name] || ''} onChange={handleChange} style={{ ...inputStyle, flex: 1, cursor: 'pointer' }}>
                      <option value="">Non renseigné</option>
                      {KIT_COLORS.map(c => <option key={c.valeur} value={c.valeur}>{c.label}</option>)}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* Niveaux de championnat par catégorie */}
          <Section title="Niveaux de championnat" isMobile={isMobile}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {categories.map(cat => {
                const s = teamLevelsSaving[cat] || 'idle'
                return (
                  <div key={cat} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '80px 1fr auto', gap: 8, alignItems: 'center' }}>
                    <div style={{ fontFamily: G.mono, fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', color: G.muted }}>{cat}</div>
                    <select
                      value={teamLevels[cat] || ''}
                      onChange={e => {
                        const v = e.target.value
                        setTeamLevels(prev => ({ ...prev, [cat]: v }))
                        setTeamLevelsSaving(prev => ({ ...prev, [cat]: 'idle' }))
                      }}
                      style={{ ...inputStyle, cursor: 'pointer' }}
                      onFocus={e => e.target.style.borderColor = T.ink}
                      onBlur={e => e.target.style.borderColor = T.rule}
                    >
                      <option value="">— Non défini —</option>
                      {(COMPETITION_LEVELS_BY_CATEGORY[cat] || ALL_COMPETITION_LEVELS).map(l => <option key={l} value={l}>{l}</option>)}
                    </select>
                    <button
                      onClick={() => handleTeamLevelSave(cat)}
                      disabled={s === 'saving'}
                      style={{
                        fontFamily: G.mono, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
                        padding: '10px 14px', whiteSpace: 'nowrap', transition: 'all .15s',
                        cursor: s === 'saving' ? 'not-allowed' : 'pointer',
                        opacity: s === 'saving' ? .6 : 1,
                        border: s === 'saved' ? `0.5px solid ${T.greenBdr}` : `0.5px solid ${T.rule}`,
                        background: s === 'saved' ? T.greenBg : T.bgAlt,
                        color: s === 'saved' ? T.green : T.ink,
                        borderRadius: T.radius,
                      }}
                    >
                      {s === 'saving' ? '...' : s === 'saved' ? '✓ Enregistré' : 'Sauver'}
                    </button>
                  </div>
                )
              })}
            </div>
            <div style={{ fontFamily: G.mono, fontSize: 9, color: G.muted, marginTop: 16 }}>
              Chaque catégorie peut avoir son propre niveau — il s'affiche sur les cartes joueurs correspondantes.
            </div>
          </Section>

          {/* Abonnement */}
          <div style={{ background: G.bg2, border: `0.5px solid ${G.border}`, borderTop: `2px solid ${T.dark}`, borderRadius: T.radius, padding: isMobile ? '20px 16px' : '24px' }}>
            <div style={{ fontFamily: T.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.14em', textTransform: 'uppercase', color: T.muted, marginBottom: 18 }}>
              Abonnement
            </div>
            <SubscriptionManagement />
          </div>

          {/* Save bar sticky bottom */}
          <div style={{
            position: 'sticky', bottom: 0, zIndex: 5,
            background: T.surface, border: `0.5px solid ${T.rule}`,
            borderTop: `2px solid ${dirty ? T.dark : T.rule}`,
            borderRadius: T.radius,
            padding: isMobile ? '14px 16px' : '14px 24px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: T.mono, fontSize: 10 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: dirty ? T.dark : T.green, display: 'inline-block' }} />
              <span style={{ color: dirty ? T.ink : T.muted }}>
                {dirty ? 'Modifications non sauvegardées' : 'Tout est sauvegardé'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button onClick={loadClub} disabled={saving || !dirty} style={{
                fontFamily: T.mono, fontSize: 9, fontWeight: 500,
                color: dirty ? T.muted : T.muted2, background: 'none', border: 'none',
                cursor: (saving || !dirty) ? 'not-allowed' : 'pointer',
              }}>
                Annuler
              </button>
              <button onClick={handleSave} disabled={saving || !dirty} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '0 20px', minHeight: 40,
                background: (saving || !dirty) ? T.muted2 : T.dark,
                color: '#FFFFFF', fontFamily: T.mono, fontSize: 10, fontWeight: 600,
                border: 'none', cursor: (saving || !dirty) ? 'not-allowed' : 'pointer',
                borderRadius: T.radius,
              }}>
                {saving ? 'Sauvegarde...' : <><Save size={13} /> Sauvegarder</>}
              </button>
            </div>
          </div>

          {/* Mot de passe */}
          <div style={{ padding: isMobile ? '10px 0' : '10px 0', display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setShowPwdModal(true)}
              style={{ fontFamily: T.mono, fontSize: 9, fontWeight: 500, color: T.muted, background: 'transparent', border: `0.5px solid ${T.rule}`, padding: '8px 14px', cursor: 'pointer', borderRadius: T.radius }}
            >
              Modifier mon mot de passe
            </button>
          </div>

          {/* Supprimer compte */}
          <div style={{ padding: isMobile ? '16px' : '16px 28px', display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={() => setShowDeleteModal(true)} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontFamily: G.mono, fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase',
              color: 'rgba(239,68,68,0.45)', display: 'flex', alignItems: 'center', gap: 6,
              transition: 'color .15s', padding: 0,
            }}
              onMouseEnter={e => e.currentTarget.style.color = 'rgba(239,68,68,0.80)'}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(239,68,68,0.45)'}>
              <Trash2 size={10} /> Supprimer mon compte
            </button>
          </div>
        </div>

        {/* Colonne aperçu carte */}
        {previewCol}
      </div>

      {/* Modale "Voir l'aperçu" du logo */}
      {showPreviewModal && (
        <div onClick={() => setShowPreviewModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: T.surface, width: '100%', maxWidth: 420, border: `0.5px solid ${T.rule}`, borderTop: `2px solid ${T.dark}`, borderRadius: T.radius }}>
            <div style={{ padding: '16px 20px', borderBottom: `0.5px solid ${T.rule}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontFamily: T.display, fontSize: 16, fontWeight: 700, textTransform: 'uppercase', color: T.ink }}>Aperçu du logo</span>
              <button onClick={() => setShowPreviewModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={16} color={T.muted} />
              </button>
            </div>
            <div style={{ padding: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 220, background: T.bgAlt }}>
              {previewLoading ? (
                <Loader2 size={28} color={T.muted} style={{ animation: 'spin 1s linear infinite' }} />
              ) : logoPreviewUrl ? (
                <img src={logoPreviewUrl} alt="Logo du club" style={{ maxWidth: '100%', maxHeight: 280, objectFit: 'contain' }} />
              ) : (
                <span style={{ fontFamily: T.mono, fontSize: 11, color: T.muted }}>Aucun logo à afficher.</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modale mot de passe */}
      {showPwdModal && <ChangePasswordModal onClose={() => setShowPwdModal(false)} />}

      {/* Modale suppression */}
      {showDeleteModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div style={{ background: T.dark, width: '100%', maxWidth: 440, border: `1px solid rgba(239,68,68,0.3)`, borderTop: `2px solid #ef4444` }}>
            <div style={{ padding: '20px 24px', borderBottom: `1px solid ${G.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <AlertTriangle size={16} color="#ef4444" />
                <span style={{ fontFamily: G.display, fontSize: 18, textTransform: 'uppercase', color: '#f5f2eb' }}>Supprimer le compte</span>
              </div>
              <button onClick={() => { setShowDeleteModal(false); setDeleteConfirm('') }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={16} color={G.muted} />
              </button>
            </div>
            <div style={{ padding: '24px' }}>
              <div style={{ padding: '14px 16px', background: 'rgba(239,68,68,0.06)', border: `1px solid rgba(239,68,68,0.15)`, marginBottom: 24 }}>
                <p style={{ fontFamily: G.mono, fontSize: 11, color: 'rgba(245,242,235,0.7)', lineHeight: 1.7, margin: 0 }}>
                  Ton compte sera désactivé immédiatement. Toutes tes données seront conservées pendant <strong style={{ color: '#f5f2eb' }}>30 jours</strong> et tu recevras un email pour récupérer ton compte si tu changes d'avis.
                </p>
              </div>
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontFamily: G.mono, fontSize: 8, letterSpacing: '.16em', textTransform: 'uppercase', color: G.muted, marginBottom: 10 }}>
                  Tape <strong style={{ color: '#ef4444' }}>SUPPRIMER</strong> pour confirmer
                </div>
                <input
                  type="text"
                  value={deleteConfirm}
                  onChange={e => setDeleteConfirm(e.target.value)}
                  placeholder="SUPPRIMER"
                  style={{
                    width: '100%', background: 'rgba(239,68,68,0.04)',
                    border: `1px solid ${deleteConfirm === 'SUPPRIMER' ? '#ef4444' : 'rgba(239,68,68,0.15)'}`,
                    padding: '12px 16px', color: '#f5f2eb',
                    fontFamily: T.mono, fontSize: 13,
                    outline: 'none', boxSizing: 'border-box', letterSpacing: '.06em',
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => { setShowDeleteModal(false); setDeleteConfirm('') }} style={{
                  flex: 1, padding: '11px', background: 'transparent',
                  border: `1px solid ${G.border}`, fontFamily: G.mono, fontSize: 9,
                  letterSpacing: '.1em', textTransform: 'uppercase', color: G.muted, cursor: 'pointer',
                }}>Annuler</button>
                <button onClick={handleDeleteAccount} disabled={deleteConfirm !== 'SUPPRIMER' || deleteLoading} style={{
                  flex: 2, padding: '11px', background: deleteConfirm === 'SUPPRIMER' ? '#ef4444' : 'rgba(239,68,68,0.2)',
                  border: 'none', fontFamily: G.mono, fontSize: 9,
                  letterSpacing: '.1em', textTransform: 'uppercase',
                  color: deleteConfirm === 'SUPPRIMER' ? '#fff' : 'rgba(239,68,68,0.4)',
                  cursor: deleteConfirm === 'SUPPRIMER' ? 'pointer' : 'not-allowed',
                  fontWeight: 700, transition: 'all .15s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}>
                  {deleteLoading ? 'Suppression...' : <><Trash2 size={11} /> Supprimer définitivement</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </DashboardLayout>
  )
}
